import { TooltipV2 } from "@/components/TooltipV2";
import { TruncatedTextWithTooltipWhenTruncated } from "@/components/data-display/TruncatedTextWithTooltipWhenTruncated";
import { Icon, type IconName } from "@/components/icons/Icon";
import { classNames } from "@/utils/classnames";
import { lightenForDarkMode } from "@/utils/color-util";
import { hashStringToInteger, mod } from "@/utils/math-utils";
import React from "react";

export const StatusBadgeV2Tones = [
  "green",
  "yellow",
  "orange",
  "red",
  "neutral",
  "blue",
  "purple",
] as const;
export type StatusBadgeV2Tone = (typeof StatusBadgeV2Tones)[number];
export const StatusBadgeV2Sizes = ["sm", "md"] as const;
export type StatusBadgeV2Size = (typeof StatusBadgeV2Sizes)[number];

const STATUS_BADGE_BASE =
  "inline-flex shrink-0 cursor-default items-center justify-center gap-cds-4 " +
  "rounded-cds-3 font-normal leading-4";

const STATUS_BADGE_BORDER = "ring-1 ring-inset";

const STATUS_BADGE_SIZES: Record<StatusBadgeV2Size, string> = {
  sm: "h-cds-16 px-cds-4 !text-cds-2xs",
  md: "h-cds-20 px-cds-6 !text-cds-xs",
};

// Placeholder pill rendered in place of the label while loading; the badge's
// height/padding come from STATUS_BADGE_SIZES, so the box stays identical.
const STATUS_BADGE_LOADING_PLACEHOLDER_SIZES: Record<StatusBadgeV2Size, string> = {
  sm: "h-cds-4 w-cds-8",
  md: "h-cds-6 w-cds-12",
};

export const STATUS_BADGE_TONES: Record<StatusBadgeV2Tone, string> = {
  green:
    "bg-cds-status-green-fill text-cds-status-green-text ring-cds-status-green-border " +
    "dark:bg-cds-status-green-fill-dark dark:text-cds-status-green-text-dark " +
    "dark:ring-cds-status-green-border-dark",
  yellow:
    "bg-cds-status-yellow-fill text-cds-status-yellow-text ring-cds-status-yellow-border " +
    "dark:bg-cds-status-yellow-fill-dark dark:text-cds-status-yellow-text-dark " +
    "dark:ring-cds-status-yellow-border-dark",
  orange:
    "bg-cds-status-orange-fill text-cds-status-orange-text ring-cds-status-orange-border " +
    "dark:bg-cds-status-orange-fill-dark dark:text-cds-status-orange-text-dark " +
    "dark:ring-cds-status-orange-border-dark",
  red:
    "bg-cds-status-red-fill text-cds-status-red-text ring-cds-status-red-border " +
    "dark:bg-cds-status-red-fill-dark dark:text-cds-status-red-text-dark " +
    "dark:ring-cds-status-red-border-dark",
  neutral:
    "bg-cds-status-neutral-fill text-cds-status-neutral-text ring-cds-status-neutral-border " +
    "dark:bg-cds-status-neutral-fill-dark dark:text-cds-status-neutral-text-dark " +
    "dark:ring-cds-status-neutral-border-dark",
  blue:
    "bg-cds-status-blue-fill text-cds-status-blue-text ring-cds-status-blue-border " +
    "dark:bg-cds-status-blue-fill-dark dark:text-cds-status-blue-text-dark " +
    "dark:ring-cds-status-blue-border-dark",
  purple:
    "bg-cds-status-purple-fill text-cds-status-purple-text ring-cds-status-purple-border " +
    "dark:bg-cds-status-purple-fill-dark dark:text-cds-status-purple-text-dark " +
    "dark:ring-cds-status-purple-border-dark",
};

const AUTOMATIC_STATUS_BADGE_TONES: StatusBadgeV2Tone[] = [
  "green",
  "neutral",
  "blue",
  "purple",
  "red",
];

const getAutomaticStatusBadgeTone = (label: string): StatusBadgeV2Tone => {
  if (label.toLowerCase() === "beta") {
    return "yellow";
  }

  return AUTOMATIC_STATUS_BADGE_TONES[
    mod(hashStringToInteger(label), AUTOMATIC_STATUS_BADGE_TONES.length)
  ];
};

/**
 * Saturated solid fill per tone, sharing the badge's palette families (the
 * `cds-status` tones above map to these). For non-badge surfaces that need a
 * bold fill rather than the badge's pale chip — e.g. progress bars / charts.
 */
export const STATUS_BADGE_TONE_SOLID_CLASS: Record<StatusBadgeV2Tone, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  orange: "bg-orange-500",
  red: "bg-rose-500",
  neutral: "bg-slate-400",
  blue: "bg-sky-500",
  purple: "bg-purple-500",
};

// Dot diameter per badge size, a little under the leading icon's box so the two variants sit on
// the same optical baseline.
const STATUS_BADGE_DOT_SIZES: Record<StatusBadgeV2Size, string> = {
  sm: "h-cds-6 w-cds-6",
  md: "h-cds-8 w-cds-8",
};

/**
 * The badge takes at most one leading element. Icons carry a fixed meaning from the icon set;
 * dots carry an arbitrary per-value color (a tag namespace, an environment) that no icon encodes.
 * Modelled as a union so passing both is a type error rather than a silent precedence rule.
 */
type StatusBadgeV2LeadingProps =
  | { leadingIcon?: IconName; leadingDot?: never }
  | { leadingIcon?: never; leadingDot: string };

export type StatusBadgeV2Props = React.HTMLAttributes<HTMLSpanElement> & {
  bordered?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  showTooltip?: boolean;
  size?: StatusBadgeV2Size;
  tone?: StatusBadgeV2Tone;
  truncate?: boolean;
  tooltip?: string;
} & StatusBadgeV2LeadingProps;

const StatusBadgeV2Base = React.forwardRef<HTMLSpanElement, StatusBadgeV2Props>(
  function StatusBadgeV2Impl(
    {
      "aria-busy": ariaBusy,
      "aria-label": ariaLabel,
      bordered = false,
      children,
      className,
      leadingDot,
      leadingIcon,
      loading = false,
      loadingLabel = "Loading status",
      showTooltip = true,
      size = "sm",
      tone,
      truncate = false,
      tooltip,
      ...rest
    },
    ref,
  ) {
    const resolvedTone =
      tone ?? (typeof children === "string" ? getAutomaticStatusBadgeTone(children) : "neutral");

    const tooltipContent = tooltip?.trim() || undefined;
    const badge = (
      <span
        {...rest}
        ref={ref}
        aria-busy={loading ? true : ariaBusy}
        aria-label={loading ? ariaLabel ?? loadingLabel : ariaLabel}
        className={classNames(
          STATUS_BADGE_BASE,
          STATUS_BADGE_SIZES[size],
          truncate && "min-w-0 max-w-full !shrink",
          bordered && STATUS_BADGE_BORDER,
          STATUS_BADGE_TONES[resolvedTone],
          className,
        )}
      >
        {loading ? (
          <span
            aria-hidden={true}
            className={classNames(
              "rounded-full bg-current opacity-60 animate-placeholder-loading",
              STATUS_BADGE_LOADING_PLACEHOLDER_SIZES[size],
            )}
          />
        ) : (
          <>
            {leadingDot != null && (
              <span
                aria-hidden={true}
                // The dot color is per-value data, not a token, so it arrives as a hex. It is
                // passed through CSS custom properties rather than an inline `background` so the
                // dark-mode value can be selected by the `dark:` variant instead of requiring the
                // component to know the active theme.
                style={
                  {
                    "--status-badge-dot": leadingDot,
                    "--status-badge-dot-dark": lightenForDarkMode(leadingDot),
                  } as React.CSSProperties
                }
                className={classNames(
                  "shrink-0 rounded-full bg-[var(--status-badge-dot)]",
                  "dark:bg-[var(--status-badge-dot-dark)]",
                  STATUS_BADGE_DOT_SIZES[size],
                )}
              />
            )}
            {leadingIcon != null && (
              <Icon name={leadingIcon} size={"xs"} className={"h-cds-10 w-cds-10 shrink-0"} />
            )}
            {truncate && typeof children === "string" ? (
              <TruncatedTextWithTooltipWhenTruncated
                className={"min-w-0"}
                showTooltip={showTooltip && tooltipContent == null}
                value={children}
              />
            ) : (
              children
            )}
          </>
        )}
      </span>
    );

    return (
      <TooltipV2 content={tooltipContent} disabled={!showTooltip || loading}>
        {badge}
      </TooltipV2>
    );
  },
);

export const StatusBadgeV2 = React.memo(StatusBadgeV2Base);
StatusBadgeV2.displayName = "StatusBadgeV2";
